// src/modules/Blog/BlogPost.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../config/supabase';
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useContactModal } from '../../contexts/ContactModalContext';
import { FaCalendar, FaEye, FaArrowLeft, FaTag, FaShareAlt, FaLinkedin, FaTwitter, FaWhatsapp } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';

// Styled Components
const PostContainer = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const HeroSection = styled.section`
  position: relative;
  height: 50vh;
  min-height: 400px;
  max-height: 500px;
  background: linear-gradient(135deg, #111 0%, #2a2a2a 100%);
  display: flex;
  align-items: flex-end;
  padding: 60px 24px;

  @media (max-width: 768px) {
    height: 45vh;
    min-height: 320px;
    max-height: 400px;
    padding: 40px 20px;
  }

  @media (max-width: 480px) {
    height: 40vh;
    min-height: 280px;
    padding: 32px 16px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${props => props.$image ? `url(${props.$image})` : 'none'};
    background-size: cover;
    background-position: center;
    opacity: 0.3;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 100%);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 24px;
  transition: color 200ms;
  width: fit-content;

  &:hover {
    color: white;
  }
`;

const PostCategory = styled.span`
  display: inline-block;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
  margin-bottom: 16px;
`;

const PostTitle = styled.h1`
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  color: white;
  line-height: 1.2;
  margin-bottom: 20px;
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);

  svg {
    font-size: 14px;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AuthorAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const AuthorName = styled.span`
  font-size: 14px;
  color: white;
  font-weight: 500;
`;

const ContentSection = styled.section`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 24px;
`;

const ArticleContent = styled.article`
  font-size: 17px;
  line-height: 1.8;
  color: #333;
  text-align: justify;

  h1, h2, h3, h4, h5, h6 {
    color: #111;
    margin-top: 40px;
    margin-bottom: 16px;
    line-height: 1.3;
  }

  h2 {
    font-size: 28px;
  }

  h3 {
    font-size: 22px;
  }

  p {
    margin-bottom: 24px;
  }

  a {
    color: #111;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  ul, ol {
    margin-bottom: 24px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  blockquote {
    border-left: 4px solid #111;
    padding-left: 24px;
    margin: 32px 0;
    font-style: italic;
    color: #555;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 32px 0;
  }

  code {
    background: #f5f5f5;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 15px;
  }

  pre {
    background: #1e1e1e;
    color: #d4d4d4;
    padding: 24px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 24px 0;

    code {
      background: none;
      padding: 0;
      color: inherit;
    }
  }
`;

const TagsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 48px;
  padding-top: 32px;
  border-top: 1px solid #eee;
`;

const TagLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;

  svg {
    font-size: 14px;
  }
`;

const Tag = styled.span`
  padding: 6px 12px;
  background: #f5f5f5;
  color: #666;
  font-size: 13px;
  border-radius: 4px;
  transition: all 200ms;

  &:hover {
    background: #eee;
  }
`;

const ShareSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 32px;
`;

const ShareLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;

  svg {
    font-size: 14px;
  }
`;

const ShareButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f5f5f5;
  color: #666;
  transition: all 200ms;

  &:hover {
    background: #111;
    color: white;
  }
`;

const CTAButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #111 0%, #333 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;
  margin: 16px 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }
`;

const LoadingState = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #666;
`;

const NotFoundState = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px;
`;

const NotFoundTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
`;

const NotFoundText = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 24px;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #111;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background 200ms;

  &:hover {
    background: #333;
  }
`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { isAdmin } = useAuth();
  const { openModal } = useContactModal();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  // Componente customizado para links - intercepta #fale-conosco
  const CustomLink = ({ href, children, ...props }) => {
    if (href === '#fale-conosco') {
      return (
        <CTAButton onClick={openModal}>
          {children}
        </CTAButton>
      );
    }
    return <a href={href} {...props}>{children}</a>;
  };

  useEffect(() => {
    fetchPost();
    // Não incrementar views se for admin
    if (!isAdmin) {
      incrementViews();
    }
  }, [slug, isAdmin]);

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (error) throw error;
      setPost(data);
    } catch (error) {
      console.error('Erro ao buscar post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const incrementViews = async () => {
    try {
      await supabase.rpc('increment_post_views', { post_slug: slug });
    } catch (error) {
      console.error('Erro ao incrementar views:', error);
    }
  };

  const formatDate = (dateString) => {
    const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR';
    return new Date(dateString).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getShareUrl = () => {
    return encodeURIComponent(window.location.href);
  };

  const getShareText = () => {
    return encodeURIComponent(getTranslatedField('title') || '');
  };

  const getViewsText = () => {
    if (language === 'en') return 'views';
    if (language === 'es') return 'vistas';
    return 'visualizações';
  };

  const getLoadingText = () => {
    if (language === 'en') return 'Loading...';
    if (language === 'es') return 'Cargando...';
    return 'Carregando...';
  };

  const getNotFoundTitle = () => {
    if (language === 'en') return 'Post not found';
    if (language === 'es') return 'Post no encontrado';
    return 'Post não encontrado';
  };

  const getNotFoundText = () => {
    if (language === 'en') return 'The post you are looking for does not exist or has been removed.';
    if (language === 'es') return 'El post que buscas no existe o fue eliminado.';
    return 'O post que você está procurando não existe ou foi removido.';
  };

  // Helper para obter conteúdo traduzido
  const getTranslatedField = (field) => {
    if (!post) return '';
    if (language === 'en' && post[`${field}_en`]) {
      return post[`${field}_en`];
    }
    if (language === 'es' && post[`${field}_es`]) {
      return post[`${field}_es`];
    }
    return post[field];
  };

  if (loading) {
    return (
      <PostContainer>
        <LoadingState>{getLoadingText()}</LoadingState>
      </PostContainer>
    );
  }

  if (!post) {
    return (
      <PostContainer>
        <NotFoundState>
          <NotFoundTitle>{getNotFoundTitle()}</NotFoundTitle>
          <NotFoundText>{getNotFoundText()}</NotFoundText>
          <BackButton to="/">
            <FaArrowLeft />
            {t('blog.backToBlog')}
          </BackButton>
        </NotFoundState>
      </PostContainer>
    );
  }

  return (
    <PostContainer>
      <HeroSection $image={post.cover_image}>
        <HeroContent>
          <BackLink to="/">
            <FaArrowLeft />
            {t('blog.backToBlog')}
          </BackLink>

          {post.category && (
            <PostCategory>{t(`blog.categories.${post.category}`) !== `blog.categories.${post.category}` ? t(`blog.categories.${post.category}`) : post.category}</PostCategory>
          )}

          <PostTitle>{getTranslatedField('title')}</PostTitle>

          <PostMeta>
            {post.author_name && (
              <AuthorInfo>
                <AuthorAvatar
                  src={post.author_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(post.author_name)}&background=333&color=fff`}
                  alt={post.author_name}
                />
                <AuthorName>{post.author_name}</AuthorName>
              </AuthorInfo>
            )}
            <MetaItem>
              <FaCalendar />
              {formatDate(post.created_at)}
            </MetaItem>
            <MetaItem>
              <FaEye />
              {post.views || 0} {getViewsText()}
            </MetaItem>
          </PostMeta>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <ArticleContent>
          <ReactMarkdown components={{ a: CustomLink }}>{getTranslatedField('content')}</ReactMarkdown>
        </ArticleContent>

        {post.tags && post.tags.length > 0 && (
          <TagsSection>
            <TagLabel>
              <FaTag />
              Tags:
            </TagLabel>
            {post.tags.map((tag, index) => (
              <Tag key={index}>{tag}</Tag>
            ))}
          </TagsSection>
        )}

        <ShareSection>
          <ShareLabel>
            <FaShareAlt />
            {t('blog.share')}:
          </ShareLabel>
          <ShareButton
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${getShareUrl()}`}
            target="_blank"
            rel="noopener noreferrer"
            title="LinkedIn"
          >
            <FaLinkedin />
          </ShareButton>
          <ShareButton
            href={`https://twitter.com/intent/tweet?url=${getShareUrl()}&text=${getShareText()}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Twitter"
          >
            <FaTwitter />
          </ShareButton>
          <ShareButton
            href={`https://wa.me/?text=${getShareText()}%20${getShareUrl()}`}
            target="_blank"
            rel="noopener noreferrer"
            title="WhatsApp"
          >
            <FaWhatsapp />
          </ShareButton>
        </ShareSection>
      </ContentSection>
    </PostContainer>
  );
};

export default BlogPost;
